import { useState, useCallback, useMemo, useEffect } from 'react';
import { useRouter } from '../../hooks/useRouter';

export type LocationType = 'queryParam' | 'segment';
export type RouterState = ReturnType<typeof useState<string | undefined>>;
export type RouterStateGetter = [RouterState[0]] | RouterState;
export type UseRouterStateProps = {
  key: string;
  defaultValue?: string;
  location?: LocationType;
};

export function useRouterState({
  key,
  defaultValue,
  location = 'queryParam',
}: UseRouterStateProps): RouterState {
  const { getParam, replaceParam, getSegment, replaceSegment } = useRouter();

  const valueOptional = useMemo(() => {
    return location === 'queryParam' ? getParam(key) : getSegment(key);
  }, [location, getParam, getSegment, key]);

  const [value, setValueInternal] = useState<string | undefined>(
    valueOptional || defaultValue,
  );

  useEffect(() => {
    setValueInternal(valueOptional || defaultValue);
  }, [valueOptional, defaultValue]);

  const setValue = useCallback(
    (
      newValue:
        | string
        | undefined
        | ((prevState: string | undefined) => string | undefined),
    ) => {
      const newValueResolved =
        typeof newValue === 'function' ? newValue(value) : newValue;
      const replace = location === 'queryParam' ? replaceParam : replaceSegment;
      replace(key, newValueResolved);
      setValueInternal(newValueResolved);
    },
    [location, replaceParam, replaceSegment, key, value],
  );

  return [value, setValue];
}
